import React, { useState, useEffect } from 'react';
import { User, Users } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import type { ReportVisibility, SavedReport } from '@/types/savedReports';

export type SaveReportMode = 'create' | 'update' | 'saveAs';

interface SaveReportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: SaveReportMode;
  existingReport?: SavedReport | null;
  onSave: (name: string, description: string, visibility: ReportVisibility) => void;
  isSaving?: boolean;
}

export const SaveReportModal: React.FC<SaveReportModalProps> = ({
  open,
  onOpenChange,
  mode,
  existingReport,
  onSave,
  isSaving = false,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<ReportVisibility>('personal');

  // Initialize form when modal opens
  useEffect(() => {
    if (open) {
      if (mode === 'update' && existingReport) {
        setName(existingReport.name);
        setDescription(existingReport.description || '');
        setVisibility(existingReport.visibility);
      } else if (mode === 'saveAs' && existingReport) {
        setName(`${existingReport.name} (Copy)`);
        setDescription(existingReport.description || '');
        setVisibility('personal'); // Copies default to personal
      } else {
        // Create mode or no existing report
        setName('');
        setDescription('');
        setVisibility('personal');
      }
    }
  }, [open, mode, existingReport]);

  const handleSave = () => {
    if (!name.trim()) return;
    onSave(name.trim(), description.trim(), visibility);
  };

  const getTitle = () => {
    switch (mode) {
      case 'create':
        return 'Save New Report';
      case 'update':
        return 'Update Report';
      case 'saveAs':
        return 'Save As New Report';
      default:
        return 'Save Report';
    }
  };

  const getButtonText = () => {
    if (isSaving) return 'Saving...';
    switch (mode) {
      case 'create':
        return 'Save Report';
      case 'update':
        return 'Update Report';
      case 'saveAs':
        return 'Save Copy';
      default:
        return 'Save';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{getTitle()}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="report-name">Name</Label>
            <Input
              id="report-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Analytics Report"
              autoFocus
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="report-description">Description (optional)</Label>
            <Textarea
              id="report-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this report..."
              rows={2}
            />
          </div>

          {/* Visibility */}
          <div className="space-y-2">
            <Label>Visibility</Label>
            <RadioGroup
              value={visibility}
              onValueChange={(value) => setVisibility(value as ReportVisibility)}
              className="space-y-2"
            >
              <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <RadioGroupItem value="personal" id="visibility-personal" />
                <Label htmlFor="visibility-personal" className="flex items-center gap-2 cursor-pointer flex-1">
                  <User className="h-4 w-4 text-gray-500" />
                  <div>
                    <div className="font-medium">Personal</div>
                    <div className="text-xs text-gray-500">Only you can see this report</div>
                  </div>
                </Label>
              </div>
              <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                <RadioGroupItem value="team" id="visibility-team" />
                <Label htmlFor="visibility-team" className="flex items-center gap-2 cursor-pointer flex-1">
                  <Users className="h-4 w-4 text-gray-500" />
                  <div>
                    <div className="font-medium">Team</div>
                    <div className="text-xs text-gray-500">Everyone on your team can view this report</div>
                  </div>
                </Label>
              </div>
            </RadioGroup>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!name.trim() || isSaving}>
            {getButtonText()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
